import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useScopedSelector } from 'ia-react-core';
import { useTranslation } from 'react-i18next';
import { getInstabilityMessages } from 'omni-package-react-cms';
import { useDateFormatter } from './useDateFormatter';
import ApplicationAvailabilityContext from '../ApplicationAvailability/ApplicationAvailabilityContext';
import WarningMessages from './WarningMessages';
import { WarningMessage } from '../../interfaces/WarningMessage';

export interface ApplicationWarningsProps {
  showInstabilityMessages?: boolean;
  showUnavailabilityMessage?: boolean;
  showUnavailabilityWarning?: boolean;
  overrideWarningDelay?: number;
  overrideMessages?: (messages: WarningMessage[]) => WarningMessage[];
}

const ApplicationWarnings: FC<ApplicationWarningsProps> = ({ showInstabilityMessages, showUnavailabilityMessage, showUnavailabilityWarning, overrideWarningDelay, overrideMessages, ...props }) => {
  const { t } = useTranslation('ApplicationWarnings');
  const language: string = useScopedSelector('/language.locale');
  const [instabilityMessages, setInstabilityMessages] = useState(null as string[]);
  const availability = useContext(ApplicationAvailabilityContext)?.availability;
  const dateFormatter = useDateFormatter();

  useEffect(() => {
    if (showInstabilityMessages) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-floating-promises
      getInstabilityMessages().then((msg: string[]) => setInstabilityMessages(msg));
    }
  }, [showInstabilityMessages, language]);

  const msg: WarningMessage[] = useMemo(() => {
    if (showInstabilityMessages && !instabilityMessages) {
      return null;
    }
    let messages: WarningMessage[] = [];
    if (showUnavailabilityMessage && availability && !availability.isAvailable) {
      let message = t('unavailableMessage');
      if (availability.unavailableUntil) {
        message += t('unavailableEndDate', { date: dateFormatter(availability.unavailableUntil, null) });
      }
      messages.push({
        title: t('unavailableMessageTitle'),
        message,
      });
    }

    if (showUnavailabilityWarning && availability?.isAvailable && availability?.unavailableFuture) {
      if (!overrideWarningDelay || (overrideWarningDelay && (availability.unavailableFuture.getTime() - new Date().getTime() < (overrideWarningDelay * 60 * 1000)))) {
        messages.push({
          title: t('unavailableWarningTitle'),
          message: t('unavailableWarning', { date: dateFormatter(availability.unavailableFuture, availability.unavailableUntil) }),
        });
      }
    }

    if (showInstabilityMessages) {
      instabilityMessages.forEach((message) => {
        messages.push({ message } as WarningMessage);
      });
    }

    if (overrideMessages) {
      messages = overrideMessages(messages);
    }
    return messages;
  }, [availability, dateFormatter, instabilityMessages, overrideMessages, overrideWarningDelay, showInstabilityMessages, showUnavailabilityMessage, showUnavailabilityWarning, t]);

  if (!msg) {
    return null;
  }

  return (
    <WarningMessages {...props} messages={msg} />
  );
};

export default ApplicationWarnings;
